import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {CreatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import {InvalidOrAbnormal} from '@di-shared/api/di/type';
import SestDetailAirCleaner from '@di-shared/features/diag/sest/SestDetailAirCleaner';
import SestDetailAirConditionerFilter from '@di-shared/features/diag/sest/SestDetailAirConditionerFilter';
import SestDetailBrakeOil from '@di-shared/features/diag/sest/SestDetailBrakeOil';
import SestDetailCoolant from '@di-shared/features/diag/sest/SestDetailCoolant';
import SestDetailEngineOilAndFilter from '@di-shared/features/diag/sest/SestDetailEngineOilAndFilter';
import SestDetailEngineOilAndFilterWarning from '@di-shared/features/diag/sest/SestDetailEngineOilAndFilterWarning';
import SestDetailEngineOilConditionalRenderer from '@di-shared/features/diag/sest/SestDetailEngineOilConditionalRenderer';
import SestExchangeHistoryManual from '@di-shared/features/diag/sest/SestExchangeHistoryManual';
import {SestCategory, SestCode} from '@di-shared/features/diag/sest/const';
import {mergeWithSDKDataAndSort} from '@di-shared/features/home/kr/vehicle/utils';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

/**
 * 카테고리 번호 (엔진:1, 제동: 2, 공조: 3, 냉각수: 4)
 * 소모품 코드 (01: 엔진오일/필터, 02:에어클리너, 03: 에어컨필터, 04: 브레이크패드, 09: 브레이크 오일)
 *
 * 교환 주기가 도래한 소모품은 제일 앞으로 이동/강조
 * - 교환 주기가 도래한 소모품 간에는 부여된 오더순으로 정렬)
 * - 소모품 명을 표시할 공간이 부족할 경우 말줄임 처리
 *
 * 고도화된 엔진 오일 데이터가 지원되는 차종일 경우,
 * 주행일자 기준 분석을 가능하게 하기 위해 최초 주행일 정보가 누락된
 * 고객 대상으로 수동으로 최초 주행 시작일 입력 동선 제공 케이스
 */
interface SestCategoryDetailProps {
  vin: string;
  ccid: string;
  sestCategory: SestCategory;
}

function SestCategoryDetail(props: SestCategoryDetailProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, ccid, sestCategory}: SestCategoryDetailProps) {
  const [{data: sestData}, {data: warningLightData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.maintenancesSestController.getAggregatedSestList1), vin, ccid],
        queryFn: () =>
          diApi.maintenancesSestController.getAggregatedSestList1({
            vin,
            ccid,
          }),
      },
      {
        queryKey: [ccWebviewUtils.getCarWarningLightStatus],
        queryFn: async () => {
          try {
            return await ccWebviewUtils.getCarWarningLightStatus();
          } catch (error) {
            return null;
          }
        },
        retry: 1,
      },
    ],
  });

  const sortedSestDataWithSDK = mergeWithSDKDataAndSort(
    sestData.data.sests.filter(({categoryNo}) => categoryNo === sestCategory),
    [
      {
        sestCode: SestCode.ENGINE_OIL_AND_FILTER,
        isSDKWarningLight: warningLightData?.engineOil === InvalidOrAbnormal.Abnormal,
      },
    ]
  );

  return (
    <>
      <main className="content page_car_management">
        <section className="section section_expendable">
          {sortedSestDataWithSDK.map(({sestCode}) => {
            switch (sestCode) {
              case CreatedMaintenanceRecordRequest.sestCode._01:
                return (
                  <SestDetailEngineOilConditionalRenderer
                    key={sestCode}
                    vin={vin}
                    ifWarningLightRender={({linkToMaintenanceAppointment}) => (
                      <SestDetailEngineOilAndFilterWarning
                        vin={vin}
                        sestCode={sestCode}
                        onClickMaintenanceAppointment={linkToMaintenanceAppointment}
                      />
                    )}
                    ifNormalRender={() => <SestDetailEngineOilAndFilter vin={vin} />}
                  />
                );
              case CreatedMaintenanceRecordRequest.sestCode._02:
                return (
                  <SestDetailAirCleaner
                    key={sestCode}
                    vin={vin}
                  />
                );
              case CreatedMaintenanceRecordRequest.sestCode._21:
                return (
                  <SestDetailCoolant
                    key={sestCode}
                    vin={vin}
                  />
                );
              case CreatedMaintenanceRecordRequest.sestCode._09:
                return (
                  <SestDetailBrakeOil
                    key={sestCode}
                    vin={vin}
                  />
                );
              case CreatedMaintenanceRecordRequest.sestCode._03:
                return (
                  <SestDetailAirConditionerFilter
                    key={sestCode}
                    vin={vin}
                  />
                );
              default:
                console.error(`Unsupported sestCode: ${sestCode}`);
                return null;
            }
          })}
        </section>
      </main>
      <ErrorBoundary fallback={<></>}>
        <Suspense fallback={<></>}>
          <SestExchangeHistoryManual vin={vin} />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default SestCategoryDetail;

function Loading() {
  return (
    <main className="content page_car_management">
      <section className="section section_expendable">
        <div
          className="section_container skeleton"
          style={{height: 230}}
        />
        <div
          className="section_container skeleton"
          style={{height: 230}}
        />
        <div
          className="section_container skeleton"
          style={{height: 230}}
        />
      </section>
    </main>
  );
}
